import { Button } from "@/components/ui/button";
import { defaultButtonClasses } from "@/constants/ui";
import { CheckCircle2 } from "lucide-react";
import { useSlotHolidayFlow } from "./SlotHolidayProvider";

type ConfirmationScreenProps = {
  onClose?: () => void;
};

const ConfirmationScreen = ({ onClose }: ConfirmationScreenProps) => {
  const { resetFlow, selectedBookingIds } = useSlotHolidayFlow();

  const handleDone = () => {
    resetFlow();
    onClose?.();
  };

  return (
    <div className={`min-h-[50vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4`}>
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-green-50 rounded-t-2xl">
        <h2 className="text-2xl font-semibold mb-2 text-center w-full">Slot Holiday Confirmed!</h2>
        <p className="text-gray-600 text-xs text-center w-full">Your slot holiday has been applied to the calendar.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1 w-full">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-green-50">
          <h2 className="text-3xl font-semibold mb-4">Slot Holiday Confirmed!</h2>
          <p className="mb-2 text-gray-600 text-sm">
            {selectedBookingIds.length}{" "}
            {selectedBookingIds.length === 1 ? "of your bookings has" : "of your bookings have"} been removed from the
            calendar.
          </p>
        </div>

        {/* Right column - Success message */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          <div className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="w-20 h-20 text-green-600 mb-6" />
            <h3 className="text-2xl font-semibold text-green-900 mb-3">Your slot holiday is confirmed.</h3>
            <p className="text-gray-700 text-center max-w-md mb-8">
              A credit note will be issued for these dates in line with our Terms &amp; Conditions, which will
              automatically be applied to future flexible bookings.
            </p>
            <div className="flex flex-col gap-3 w-full max-w-sm">
              <Button className={`w-full ${defaultButtonClasses}`} onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6 w-full min-w-screen">
        <div className="flex flex-col items-center justify-center py-12">
          <CheckCircle2 className="w-20 h-20 text-green-600 mb-6" />
          <h3 className="text-2xl font-semibold text-green-900 mb-3">Your slot holiday is confirmed.</h3>
          <p className="text-gray-700 text-center mb-2">
            A credit note will be issued for these dates in line with our Terms &amp; Conditions, which will
            automatically be applied to future flexible bookings.
          </p>
          <p className="text-gray-600 text-center text-sm mb-8">
            {selectedBookingIds.length}{" "}
            {selectedBookingIds.length === 1 ? "of your bookings has" : "of your bookings have"} been removed from the
            calendar.
          </p>
          <div className="flex flex-col gap-3 w-full">
            <Button className={`w-full ${defaultButtonClasses}`} onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationScreen;
